'use client';

import { c as _c } from "react/compiler-runtime";
import Script from 'next/script';
import { useState, useRef, useCallback, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass } from '@awesome.me/kit-94e593bf23/icons/classic/regular';
import { useGooglePlaces } from '@/app/_lib/hooks/useGooglePlaces';
import { GOOGLE_PLACES_API_KEY } from '@/app/_lib/config/google';

/**
 * Reusable Google Places Autocomplete component
 *
 * @param {Object} props
 * @param {string} props.id - Input field id
 * @param {string} props.name - Input field name
 * @param {string} props.label - Label text
 * @param {Function} props.onPlaceSelected - Callback when address is selected: (addressData) => void
 *   addressData shape: { street_1, city, state, zip, country }
 * @param {boolean} props.required - Whether field is required
 * @param {string} props.defaultValue - Default value for input
 * @param {string} props.value - Controlled value for input
 * @param {Function} props.onChange - Additional onChange handler
 * @param {string} props.className - Additional classes for input
 * @param {boolean} props.disabled - Whether input is disabled
 * @param {Object} props.ariaProps - ARIA attributes (aria-required, aria-invalid, aria-describedby)
 * @param {Object} inputRef - Optional external ref for the input element
 */
const GooglePlacesAutocomplete = t0 => {
  const $ = _c(53);
  const {
    id,
    name,
    label,
    onPlaceSelected,
    required: t1,
    defaultValue,
    value,
    onChange,
    className: t2,
    disabled: t3,
    ariaProps: t4,
    inputRef: externalRef,
    variant: t5
  } = t0;
  const required = t1 === undefined ? false : t1;
  const className = t2 === undefined ? "" : t2;
  const disabled = t3 === undefined ? false : t3;
  let t6;
  if ($[0] !== t4) {
    t6 = t4 === undefined ? {} : t4;
    $[0] = t4;
    $[1] = t6;
  } else {
    t6 = $[1];
  }
  const ariaProps = t6;
  const variant = t5 === undefined ? "default" : t5;
  let t7;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = [];
    $[2] = t7;
  } else {
    t7 = $[2];
  }
  const [addressSuggestions, setAddressSuggestions] = useState(t7);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
  const internalInputRef = useRef(null);
  const inputRef = externalRef || internalInputRef;
  const suggestionsRef = useRef(null);
  const listId = `${id}-suggestions`;
  const listOpen = showSuggestions && addressSuggestions.length > 0;
  const {
    getAutocompleteSuggestions,
    getPlaceDetails,
    parseAddressComponents
  } = useGooglePlaces();
  let t8;
  if ($[3] !== getAutocompleteSuggestions || $[4] !== onChange) {
    t8 = e => {
      const value_0 = e.target.value;
      if (onChange) {
        onChange(e);
      }
      getAutocompleteSuggestions(value_0, predictions => {
        setAddressSuggestions(predictions);
        setShowSuggestions(predictions.length > 0);
        setSelectedSuggestionIndex(-1);
      });
    };
    $[3] = getAutocompleteSuggestions;
    $[4] = onChange;
    $[5] = t8;
  } else {
    t8 = $[5];
  }
  const handleInputChange = t8;
  let t9;
  if ($[6] !== getPlaceDetails || $[7] !== inputRef || $[8] !== onPlaceSelected || $[9] !== parseAddressComponents) {
    t9 = suggestion => {
      getPlaceDetails(suggestion, place => {
        if (place) {
          const addressData = parseAddressComponents(place);
          if (addressData) {
            if (inputRef.current) {
              inputRef.current.value = addressData.street_1;
            }
            if (onPlaceSelected && typeof onPlaceSelected === "function") {
              onPlaceSelected(addressData);
            }
          }
        }
        setShowSuggestions(false);
        setAddressSuggestions([]);
      });
    };
    $[6] = getPlaceDetails;
    $[7] = inputRef;
    $[8] = onPlaceSelected;
    $[9] = parseAddressComponents;
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  const handleSuggestionClick = t9;
  let t10;
  if ($[11] !== addressSuggestions || $[12] !== handleSuggestionClick || $[13] !== selectedSuggestionIndex || $[14] !== showSuggestions) {
    t10 = e_0 => {
      if (!showSuggestions || addressSuggestions.length === 0) {
        return;
      }
      bb55: switch (e_0.key) {
        case "ArrowDown":
          {
            e_0.preventDefault();
            setSelectedSuggestionIndex(prev_0 => prev_0 < addressSuggestions.length - 1 ? prev_0 + 1 : prev_0);
            break bb55;
          }
        case "ArrowUp":
          {
            e_0.preventDefault();
            setSelectedSuggestionIndex(_temp);
            break bb55;
          }
        case "Enter":
          {
            if (selectedSuggestionIndex >= 0) {
              e_0.preventDefault();
              handleSuggestionClick(addressSuggestions[selectedSuggestionIndex]);
            }
            break bb55;
          }
        case "Escape":
          {
            e_0.preventDefault();
            e_0.stopPropagation();
            setShowSuggestions(false);
            setSelectedSuggestionIndex(-1);
          }
      }
    };
    $[11] = addressSuggestions;
    $[12] = handleSuggestionClick;
    $[13] = selectedSuggestionIndex;
    $[14] = showSuggestions;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  const handleKeyDown = t10;
  let t11;
  let t12;
  if ($[16] !== inputRef) {
    t11 = () => {
      const handleClickOutside = event => {
        if (suggestionsRef.current && !suggestionsRef.current.contains(event.target) && inputRef.current && !inputRef.current.contains(event.target)) {
          setShowSuggestions(false);
          setSelectedSuggestionIndex(-1);
        }
      };
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    };
    t12 = [inputRef];
    $[16] = inputRef;
    $[17] = t11;
    $[18] = t12;
  } else {
    t11 = $[17];
    t12 = $[18];
  }
  useEffect(t11, t12);
  let t13;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = GOOGLE_PLACES_API_KEY && <Script src={`https://maps.googleapis.com/maps/api/js?key=${GOOGLE_PLACES_API_KEY}&libraries=places&loading=async`} strategy="lazyOnload" />;
    $[19] = t13;
  } else {
    t13 = $[19];
  }
  const t14 = `${variant === "material" ? "material-input-group" : "form-group"} mb-4 relative`;
  const t15 = `${variant === "material" ? "material-input" : "form-input"} peer pr-10 ${className}`;
  const t16 = listOpen ? listId : undefined;
  const t17 = listOpen && selectedSuggestionIndex >= 0 ? `${listId}-${selectedSuggestionIndex}` : undefined;
  let t18;
  if ($[20] !== ariaProps || $[21] !== defaultValue || $[22] !== disabled || $[23] !== handleInputChange || $[24] !== handleKeyDown || $[25] !== id || $[26] !== inputRef || $[27] !== listOpen || $[28] !== name || $[29] !== required || $[30] !== t15 || $[31] !== t16 || $[32] !== t17 || $[33] !== value) {
    t18 = <input ref={inputRef} id={id} type="text" name={name} autoComplete="street-address-search" className={t15} placeholder=" " required={required} defaultValue={defaultValue} value={value} onChange={handleInputChange} onKeyDown={handleKeyDown} disabled={disabled} role="combobox" aria-autocomplete="list" aria-expanded={listOpen} aria-controls={t16} aria-activedescendant={t17} {...ariaProps} />;
    $[20] = ariaProps;
    $[21] = defaultValue;
    $[22] = disabled;
    $[23] = handleInputChange;
    $[24] = handleKeyDown;
    $[25] = id;
    $[26] = inputRef;
    $[27] = listOpen;
    $[28] = name;
    $[29] = required;
    $[30] = t15;
    $[31] = t16;
    $[32] = t17;
    $[33] = value;
    $[34] = t18;
  } else {
    t18 = $[34];
  }
  const t19 = variant === "material" ? "material-input-label peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-not-placeholder-shown:material-input-label-float" : "form-input-label peer-placeholder-shown:form-input-label-placeholder-shown";
  let t20;
  if ($[35] !== id || $[36] !== label || $[37] !== t19) {
    t20 = <label htmlFor={id} className={t19}>{label}</label>;
    $[35] = id;
    $[36] = label;
    $[37] = t19;
    $[38] = t20;
  } else {
    t20 = $[38];
  }
  const t21 = `absolute text-gray-400 text-sm pointer-events-none ${variant === "material" ? "right-0 top-5" : "right-3 top-1/2 -translate-y-1/2"}`;
  let t22;
  if ($[39] !== t21) {
    t22 = <FontAwesomeIcon icon={faMagnifyingGlass} className={t21} />;
    $[39] = t21;
    $[40] = t22;
  } else {
    t22 = $[40];
  }
  let t23;
  if ($[41] !== addressSuggestions || $[42] !== handleSuggestionClick || $[43] !== listId || $[44] !== listOpen || $[45] !== selectedSuggestionIndex) {
    t23 = listOpen && <div ref={suggestionsRef} id={listId} role="listbox" aria-label="Address suggestions" className="absolute z-50 w-3/4 bg-white rounded mt-1 max-h-60 overflow-y-auto shadow-lg" style={{
      top: "100%",
      left: 0
    }}>{addressSuggestions.map((suggestion_0, index) => <div key={suggestion_0.place_id} id={`${listId}-${index}`} role="option" aria-selected={index === selectedSuggestionIndex} onClick={() => handleSuggestionClick(suggestion_0)} className={`px-4 py-3 cursor-pointer text-3xs text-light transition-colors ${index === selectedSuggestionIndex ? "bg-gray-5" : "hover:bg-gray-5"} ${index > 0 ? "border-t-1 border-gray-8" : ""}`}><div className="font-medium text-dark">{suggestion_0.structured_formatting.main_text}</div><div className="text-light">{suggestion_0.structured_formatting.secondary_text}</div></div>)}</div>;
    $[41] = addressSuggestions;
    $[42] = handleSuggestionClick;
    $[43] = listId;
    $[44] = listOpen;
    $[45] = selectedSuggestionIndex;
    $[46] = t23;
  } else {
    t23 = $[46];
  }
  let t24;
  if ($[47] !== t14 || $[48] !== t18 || $[49] !== t20 || $[50] !== t22 || $[51] !== t23) {
    t24 = <>{t13}<div className={t14}>{t18}{t20}{t22}{t23}</div></>;
    $[47] = t14;
    $[48] = t18;
    $[49] = t20;
    $[50] = t22;
    $[51] = t23;
    $[52] = t24;
  } else {
    t24 = $[52];
  }
  return t24;
};
export default GooglePlacesAutocomplete;
function _temp(prev) {
  return prev > 0 ? prev - 1 : -1;
}